import { trpc } from '@/shared/api/trpc';
import { notificationListInput } from './use-notifications';

// Mark ONE notification read.
//
// Applied optimistically so the row and the badge settle in the same frame the
// user clicks — waiting for the round trip (let alone the 60s poll) reads as a
// dead control. The server is idempotent (it keeps the first read time), so a
// double click is harmless, and `onSettled` reconciles with the truth either way.
export const useMarkRead = () => {
  const utils = trpc.useUtils();

  return trpc.notifications.markRead.useMutation({
    onMutate: async ({ id }) => {
      // Stop in-flight refetches from landing on top of the optimistic edit.
      await Promise.all([
        utils.notifications.list.cancel(),
        utils.notifications.unreadCount.cancel(),
      ]);

      const previousList = utils.notifications.list.getInfiniteData(
        notificationListInput
      );
      const previousCount = utils.notifications.unreadCount.getData();

      // Only a row that is actually still unread may move the counter —
      // otherwise re-clicking a read row would drive the badge negative.
      const wasUnread =
        previousList?.pages.some((page) =>
          page.data.some((row) => row.id === id && row.readOn === null)
        ) ?? false;

      const readOn = new Date().toISOString();
      utils.notifications.list.setInfiniteData(notificationListInput, (old) =>
        old
          ? {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                data: page.data.map((row) =>
                  row.id === id && row.readOn === null
                    ? { ...row, readOn }
                    : row
                ),
              })),
            }
          : old
      );

      if (wasUnread) {
        utils.notifications.unreadCount.setData(undefined, (count) =>
          Math.max(0, (count ?? 0) - 1)
        );
      }

      return { previousList, previousCount };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousList !== undefined) {
        utils.notifications.list.setInfiniteData(
          notificationListInput,
          context.previousList
        );
      }
      if (context?.previousCount !== undefined) {
        utils.notifications.unreadCount.setData(
          undefined,
          context.previousCount
        );
      }
    },

    onSettled: () => {
      utils.notifications.list.invalidate();
      utils.notifications.unreadCount.invalidate();
    },
  });
};
